'use client';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/utils/chunk-error';

/**
 * The route error boundary shared by both documents. Each root document group exports it
 * from its own `error.tsx` (`src/app/(shell)/error.tsx`, `src/app/[locale]/error.tsx`),
 * so it renders inside that document's `<html>`, `<body>` and intl provider. It must not
 * sit at `src/app/error.tsx`: above the pass-through root layout there is no document and
 * no intl context, `useTranslations` would throw, and `global-error` would receive that
 * error instead of the original one (losing the digest and the chunk-error reload).
 */
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations('common.errorBoundary');
  const isChunkError = isChunkLoadError(error);

  useEffect(() => {
    // Capture the error with PostHog
    trackingManager.captureException(error, {
      error_boundary: 'route',
      error_digest: error.digest,
      timestamp: new Date().toISOString(),
    });
  }, [error]);

  useEffect(() => {
    // A rejected chunk stays rejected until the document reloads, so reset() cannot recover.
    if (isChunkError) reloadOnceForChunkError();
  }, [isChunkError]);

  return (
    <div className="min-h-safe-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{t('description')}</p>
          <Button
            onClick={() => (isChunkError ? window.location.reload() : reset())}
            className="w-full"
          >
            {t('tryAgain')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
